import React, { useState } from 'react';
import {
  Icon,
  PopOverWrapper,
  PopOverTrigger,
  PopOverContent,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Divider,
  UtilsBlock,
} from '@hz-design-system/web-ui';
import { Trans } from '@ecg-marktplaats/js-react-i18n';
import SaveSellerControl from '@/client/components/SellerHeader/SaveSellerControl';
import getIsMobile from '@/client/utils/browser/isMobile';

import Reviews from './Reviews';

type TInfoProps = {
  sellerId: number;
  sellerLocation?: string;
  reviews?: TReviewItem | null;
  reviewsLocationCount: number;
  isSavedSeller: boolean;
  onClickReviewLink: () => void;
  onClickSaveControl: (() => Promise<void>) | (() => false | void);
  isSaveSellerControlVisible: boolean;
  saveControlLoading?: boolean;
  periodSinceRegistrationDate: string;
  dsaInformation?: TDsaInformation;
  paidProfile: boolean;
};

const Info = ({
  sellerId,
  sellerLocation = '',
  reviews = null,
  reviewsLocationCount = 0,
  isSavedSeller,
  onClickReviewLink,
  isSaveSellerControlVisible,
  onClickSaveControl,
  saveControlLoading = false,
  periodSinceRegistrationDate,
  dsaInformation,
  paidProfile,
}: TInfoProps) => {
  const [shouldShowPopover, setShouldShowPopover] = useState(false);
  const { approved, companyRegistrationName, taxNumber, phoneNumber, email, streetAndHouseNumber, postcode, city } =
    dsaInformation || {};

  const popOverHandler = () => {
    setShouldShowPopover(!shouldShowPopover);
  };

  const handleClickButton = (evt) => {
    evt.preventDefault();
    onClickSaveControl();
  };

  const PaidProfileInfoComponent = () => (
    <div className="seller-header-info-paid">
      <div className="info-wrapper">
        <Trans className="item" i18nKey="soi.sellerHeader.joinedSince" value={periodSinceRegistrationDate} />
        {reviews && (
          <UtilsBlock textStyle="body-large">
            <Reviews
              reviews={reviews}
              reviewsLocationCount={reviewsLocationCount}
              onClickReviewLink={onClickReviewLink}
            />
          </UtilsBlock>
        )}
        {sellerLocation && (
          <div className="item">
            <Icon name="location" />
            {sellerLocation}
          </div>
        )}
      </div>
      {isSaveSellerControlVisible && (
        <SaveSellerControl
          sellerId={sellerId}
          isSavedSeller={isSavedSeller}
          onClick={handleClickButton}
          loading={saveControlLoading}
        />
      )}
    </div>
  );

  const FreeProfileInfoComponent = () => (
    <>
      {getIsMobile() && approved ? (
        <Accordion className="seller-header-info-free__mobile-dsa">
          <AccordionItem>
            <AccordionHeader>
              <div className="accordion-header">
                <UtilsBlock textStyle="body-large">
                  <UtilsBlock color="text-secondary" textStyle="body-regular">
                    <Trans
                      className="joined-since"
                      i18nKey="soi.sellerHeader.joinedSince"
                      value={periodSinceRegistrationDate}
                    />
                  </UtilsBlock>

                  <Reviews
                    reviews={reviews}
                    reviewsLocationCount={reviewsLocationCount}
                    onClickReviewLink={onClickReviewLink}
                  />
                </UtilsBlock>
                {isSaveSellerControlVisible && (
                  <SaveSellerControl
                    sellerId={sellerId}
                    isSavedSeller={isSavedSeller}
                    onClick={handleClickButton}
                    loading={saveControlLoading}
                  />
                )}
              </div>
            </AccordionHeader>
            <AccordionBody className="accordion-body">
              <div className="accordion-body-content">
                <Divider />
                {companyRegistrationName && (
                  <div className="item">
                    <div className="value company-name">
                      <Trans tagName="span" i18nKey="dsa.companyName" />
                      {`: ${companyRegistrationName}`}
                    </div>
                  </div>
                )}
                {taxNumber && (
                  <div className="item">
                    <Trans tagName="div" i18nKey="dsa.taxNumber" />
                    <div className="value">{`: ${taxNumber}`}</div>
                  </div>
                )}
                <div className="item">
                  <Icon name="checkmark-circled-line" className="icon" color="text-primary" />
                  <Trans className="value" tagName="div" i18nKey="dsa.approved.title" />
                </div>

                <Divider />

                {phoneNumber && (
                  <div className="item">
                    <Icon name="call" className="icon" color="text-primary" />
                    <div className="value">{phoneNumber}</div>
                  </div>
                )}
                {email && (
                  <div className="item">
                    <Icon name="email" className="icon" color="text-primary" />
                    <div className="value">{email}</div>
                  </div>
                )}
                {(streetAndHouseNumber || postcode || city) && (
                  <div className="item">
                    <Icon name="location" className="icon" color="text-primary" />
                    <div>
                      <div className="value">{`${streetAndHouseNumber && streetAndHouseNumber}${(postcode || city) && `, ${postcode} ${city}`}`}</div>
                    </div>
                  </div>
                )}
              </div>
            </AccordionBody>
          </AccordionItem>
        </Accordion>
      ) : (
        <div className="seller-header-info-free__others">
          <UtilsBlock textStyle="body-large">
            <UtilsBlock color="text-secondary" textStyle="body-regular">
              <Trans i18nKey="soi.sellerHeader.joinedSince" value={periodSinceRegistrationDate} />
            </UtilsBlock>
            <Reviews
              reviews={reviews}
              reviewsLocationCount={reviewsLocationCount}
              onClickReviewLink={onClickReviewLink}
            />
          </UtilsBlock>

          {sellerLocation && !approved && (
            <div className="item">
              <Icon name="location" />
              {sellerLocation}
            </div>
          )}
          {approved && (
            <div className="dsa-items">
              {companyRegistrationName && (
                <div className="item">
                  <Icon name="shop" className="icon" />
                  <div>
                    <Trans className="title" tagName="div" i18nKey="dsa.companyName" />
                    <div className="value  company-name">{companyRegistrationName}</div>
                  </div>
                </div>
              )}
              {phoneNumber && (
                <div className="item">
                  <Icon name="call" className="icon" />
                  <div>
                    <Trans className="title" tagName="div" i18nKey="dsa.phoneNumber" />
                    <div className="value">{phoneNumber}</div>
                  </div>
                </div>
              )}
              {email && (
                <div className="item">
                  <Icon name="email" className="icon" />
                  <div>
                    <Trans className="title" tagName="div" i18nKey="dsa.emailAddress" />
                    <div className="value">{email}</div>
                  </div>
                </div>
              )}
              {(streetAndHouseNumber || postcode || city) && (
                <div className="item">
                  <Icon name="location" className="icon" />
                  <div>
                    <Trans className="title" tagName="div" i18nKey="dsa.contactDetails" />
                    {streetAndHouseNumber && <div className="value">{streetAndHouseNumber}</div>}
                    {(postcode || city) && (
                      <div className="value">
                        {postcode}
                        {postcode && ' '}
                        {city}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {taxNumber && (
                <div className="item">
                  <Icon name="id" className="icon" />
                  <div>
                    <Trans className="title" tagName="div" i18nKey="dsa.taxNumber" />
                    <div className="value">{taxNumber}</div>
                  </div>
                </div>
              )}
              <div className="eu-compliance-item">
                <div className="icon">
                  <Icon name="checkmark-circled-line" />
                </div>
                <Trans className="title" tagName="div" i18nKey="dsa.approved.title" />
                <PopOverWrapper placement="bottom" openOnLoad={shouldShowPopover}>
                  <PopOverTrigger>
                    <Icon
                      onClick={popOverHandler}
                      name="info"
                      color="background-controls-selected"
                      className="info-icon"
                    />
                  </PopOverTrigger>
                  <PopOverContent onClose={popOverHandler}>
                    <Trans className="dsaApprovedPopover" tagName="div" i18nKey="dsa.approved.description" />
                  </PopOverContent>
                </PopOverWrapper>
              </div>
            </div>
          )}
          {isSaveSellerControlVisible && (
            <SaveSellerControl
              sellerId={sellerId}
              isSavedSeller={isSavedSeller}
              onClick={handleClickButton}
              loading={saveControlLoading}
            />
          )}
        </div>
      )}
    </>
  );

  return paidProfile ? <PaidProfileInfoComponent /> : <FreeProfileInfoComponent />;
};

export default Info;
